import React, { useEffect, useState } from 'react'
import { Box, Alert, CircularProgress, Tabs, Tab } from '@mui/material'
import { apiService } from '../services/api'
import {
  ReportPage, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, Icon, fmt,
  useDistinctFilters, buildOplFilterFields,
} from '../components/report-shell'

// Region / Dystrykt — podsumowanie MTD (Orange).
//
// Focused view over the same dataset as R1 (`/sales/orange-region-daily`),
// showing only the "Tabela 3"-style region/district rollup the business
// team needs for their monthly regional review — no salon-level or
// per-agent/per-KAM noise. Use "R1 · Sprzedaż dnia" for the operational,
// salon-level view.

const yesterdayIso = () => {
  const d = new Date(); d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

// Mirrors "ŚREDNIA TOTAL — POLSKA" at the bottom of her "Analiza Regiony"
// sheet — the totals were already correct (used in the KPI strip), just not
// shown as a row next to the region/district table itself.
const PolandTotalLine = ({ t, pctFmt }) => {
  if (!t) return null
  return (
    <Box sx={{
      display: 'flex', gap: 3, flexWrap: 'wrap', px: 2, py: 1.25, mt: 1,
      fontSize: 13, color: '#7c2d12', background: '#fff7ed',
      borderTop: '2px solid #ff7900', borderRadius: '0 0 4px 4px',
      fontWeight: 700,
    }}>
      <span>POLSKA — RAZEM</span>
      <span>Salonów: {t.salon_count}</span>
      <span>TOTAL (dzień): {t.total_day}</span>
      <span>TOTAL (MTD): {t.total_mtd}</span>
      <span>Bez sprzedaży (MTD): {t.zero_mtd_count} ({pctFmt(t.pct_zero_mtd)})</span>
      <span>Śr. realizacja/POS: {(t.avg_realization_per_pos || 0).toFixed(2)}</span>
      <span>OPOS (MTD): {t.opos_mtd} ({pctFmt(t.pct_opos_mtd)})</span>
      <span>Lokalizator (MTD): {t.loc_mtd} ({pctFmt(t.pct_loc_mtd)})</span>
    </Box>
  )
}

const OrangeRegionSummaryPage = () => {
  const [day, setDay] = useState(yesterdayIso())
  const [filters, setFilters] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)

  const distinct = useDistinctFilters('opl')
  const allFilters = () => ({ day, ...filters })

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const resp = await apiService.getOrangeRegionDaily(allFilters())
      setData(resp.data)
    } catch (err) {
      // Drop the previous result set: otherwise a failed refetch leaves the
      // old filter's numbers on screen underneath the new filter chips.
      setData(null)
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true)
    try {
      const resp = await apiService.exportOrangeRegionDaily(allFilters())
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Sprzedaz_dzienna_OPL_${day}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [day, JSON.stringify(filters)])

  const T = data?.totals || {}

  // pct_* fields come from the backend as fractions (0–1) — render as text,
  // not the semantic `percent` badge (that column type assumes "higher is
  // better", which is backwards for a zero-sales share).
  const pctFmt = (v) => fmt.pct((Number(v) || 0) * 100)

  const rollupCols = (labelField, label) => [
    { field: labelField, header: label, width: '1.6fr', minWidth: 180 },
    // A district row carries its containing region; the region tab doesn't
    // need a parent column.
    ...(labelField === 'dystrykt'
      ? [{ field: 'region', header: 'Region', width: '130px' }]
      : []),
    { field: 'salon_count', header: 'Salonów', width: '90px', numeric: true },
    { field: 'total_day', header: 'TOTAL (dzień)', width: '120px', sales: true },
    { field: 'total_mtd', header: 'TOTAL (MTD)', width: '115px', numeric: true },
    { field: 'zero_mtd_count', header: 'Salony bez sprzedaży (MTD)', width: '170px', numeric: true },
    { field: 'pct_zero_mtd', header: 'Udział % zerówek', width: '140px', numeric: true, format: pctFmt },
    { field: 'avg_realization_per_pos', header: 'Śr. realizacja / POS', width: '150px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'opos_mtd', header: 'OPOS (MTD)', width: '110px', numeric: true },
    { field: 'loc_mtd', header: 'Lokalizator (MTD)', width: '130px', numeric: true },
    { field: 'avg_opos_per_pos', header: 'Śr. OPOS / POS', width: '130px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'avg_loc_per_pos', header: 'Śr. Lokalizator / POS', width: '155px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'pct_opos_mtd', header: 'Udział % OPOS', width: '120px', numeric: true, format: pctFmt },
    { field: 'pct_loc_mtd', header: 'Udział % Lokalizator', width: '140px', numeric: true, format: pctFmt },
  ]

  const filterBar = (
    <FilterBar
      periods={[
        { type: 'date', key: 'day', label: 'Dzień raportu', value: day, onChange: (v) => setDay(v || yesterdayIso()) },
      ]}
      periodRight={data?.month_start ? `MTD od ${data.month_start}` : null}
      filters={buildOplFilterFields(distinct, filters, setFilters, { omit: ['dystrykt'] })}
      onReset={() => setFilters({})}
    />
  )

  const kpiTiles = data ? [
    { key: 'salons', label: 'Salony w zakresie', value: fmt.int(T.salon_count || 0) },
    { key: 'mtd', label: 'Sprzedaż MTD', value: fmt.int(T.total_mtd || 0), unit: 'szt.', accent: '#ff7900',
      sub: `OPOS ${fmt.int(T.opos_mtd || 0)} · Loc ${fmt.int(T.loc_mtd || 0)}` },
    { key: 'zero', label: 'Salony bez sprzedaży (MTD)',
      value: `${T.zero_mtd_count || 0} / ${T.salon_count || 0}`, accent: '#dc2626',
      sub: fmt.pct((T.pct_zero_mtd || 0) * 100) },
    { key: 'real', label: 'Śr. realizacja / POS', value: (T.avg_realization_per_pos || 0).toFixed(2), unit: 'szt.', accent: '#2563eb' },
  ] : []

  const tabConfigs = [
    { label: `Regiony (${data?.regions?.length || 0})`, rows: data?.regions || [], cols: rollupCols('region', 'Region'), keyField: 'region' },
    { label: `Dystrykty (${data?.districts?.length || 0})`, rows: data?.districts || [], cols: rollupCols('dystrykt', 'Dystrykt'), keyField: 'dystrykt' },
  ]
  const active = tabConfigs[tab]

  return (
    <ReportPage
      title="Orange — Region / Dystrykt (podsumowanie)"
      subtitle="Rollup sprzedaży MTD per region i dystrykt — salony bez sprzedaży, śr. realizacja/POS, podział OPOS/Lokalizator."
      breadcrumb={['KAM-SFA', 'Orange', 'Region / Dystrykt']}
      actions={
        <>
          <HeaderButton onClick={fetchData} disabled={loading}>
            <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
          </HeaderButton>
          <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
            <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
          </HeaderButton>
        </>
      }
      filterBar={filterBar}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && !loading && (
        <Section
          title="Rollup region / dystrykt"
          right={
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              {tabConfigs.map((t, i) => <Tab key={i} label={t.label} />)}
            </Tabs>
          }
        >
          <ReportTable
            rows={active.rows.map((r, i) => ({ id: r[active.keyField] || i, ...r }))}
            columns={active.cols}
            initialSort={{ field: 'total_mtd', dir: 'desc' }}
            pageSize={25}
            pageSizeOptions={[25, 50, 100]}
            density="compact"
          />
          <PolandTotalLine t={T} pctFmt={pctFmt} />
        </Section>
      )}
    </ReportPage>
  )
}

export default OrangeRegionSummaryPage
